import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, X, Check, Film, GripVertical, Calendar, Clock, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminClassVideos = ({ courseSlug }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', video_url: '', class_date: '', duration: '', description: '', thumbnail_url: '' });

  useEffect(() => {
    fetchVideos();
  }, [courseSlug]);

  const fetchVideos = async () => {
    try {
      const res = await api.get(`/special-courses/${courseSlug}/class-videos`);
      setVideos(res.data.videos || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!form.video_url || !form.title) {
      toast.error('Título y URL son obligatorios');
      return;
    }
    try {
      const res = await api.post(`/admin/special-courses/${courseSlug}/class-videos`, form);
      setVideos(prev => [res.data, ...prev]);
      resetForm();
      toast.success('Vídeo añadido');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al añadir vídeo');
    }
  };

  const handleUpdate = async (videoId) => {
    try {
      const res = await api.put(`/admin/special-courses/${courseSlug}/class-videos/${videoId}`, form);
      setVideos(prev => prev.map(v => v.id === videoId ? res.data : v));
      resetForm();
      toast.success('Vídeo actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('¿Eliminar este vídeo?')) return;
    try {
      await api.delete(`/admin/special-courses/${courseSlug}/class-videos/${videoId}`);
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Vídeo eliminado');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const startEdit = (video) => {
    const source = video.video_source || 'youtube';
    const vid = video.video_id || video.youtube_video_id;
    let url = '';
    if (source === 'youtube') url = `https://youtu.be/${vid}`;
    else if (source === 'drive') url = `https://drive.google.com/file/d/${vid}/view`;

    setEditingId(video.id);
    setShowForm(true);
    setForm({
      title: video.title,
      video_url: url,
      class_date: video.class_date || '',
      duration: video.duration || '',
      description: video.description || '',
      thumbnail_url: video.thumbnail_url || '',
    });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ title: '', video_url: '', class_date: '', duration: '', description: '', thumbnail_url: '' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const getSourceLabel = (video) => {
    const source = video.video_source || (video.youtube_video_id ? 'youtube' : '?');
    if (source === 'youtube') return 'YouTube';
    if (source === 'drive') return 'Google Drive';
    return source;
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="card-soft" data-testid="admin-class-videos">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">Clases en Vídeo</h2>
          <p className="text-sm text-[hsl(var(--text-tertiary))]">{videos.length} {videos.length === 1 ? 'vídeo' : 'vídeos'} publicados</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => { setShowForm(true); setEditingId(null); setForm({ title: '', video_url: '', class_date: new Date().toISOString().split('T')[0], duration: '', description: '', thumbnail_url: '' }); }}
            className="btn-gold gap-2"
            data-testid="admin-add-video-btn"
          >
            <Plus className="w-4 h-4" /> Añadir vídeo
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-6 bg-[hsl(var(--sage))]/10 border border-[hsl(var(--sage))]/20 rounded-xl p-5" data-testid="admin-video-form">
          <h3 className="text-sm font-medium text-[hsl(var(--text-primary))] mb-3">{editingId ? 'Editar vídeo' : 'Nuevo vídeo de clase'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Título *</label>
              <Input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Clase 1 - Iniciación al Sostén"
                data-testid="admin-video-title"
              />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">URL del vídeo * (YouTube o Google Drive)</label>
              <Input
                value={form.video_url}
                onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
                placeholder="https://youtu.be/... o https://drive.google.com/file/d/..."
                data-testid="admin-video-url"
              />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Fecha de la clase</label>
              <Input
                type="date"
                value={form.class_date}
                onChange={e => setForm(f => ({ ...f, class_date: e.target.value }))}
                data-testid="admin-video-date"
              />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Duración</label>
              <Input
                value={form.duration}
                onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                placeholder="1h 30min"
                data-testid="admin-video-duration"
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Descripción (opcional)</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del contenido..."
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/30 focus:border-[hsl(var(--accent))] resize-none"
              data-testid="admin-video-description"
            />
          </div>
          <div className="mt-3">
            <label className="text-xs text-[hsl(var(--text-tertiary))] mb-1 block">Imagen de portada (opcional — URL de Cloudinary u otra)</label>
            <Input
              value={form.thumbnail_url}
              onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))}
              placeholder="https://res.cloudinary.com/... (dejar vacío para miniatura automática)"
              data-testid="admin-video-thumbnail"
            />
            {form.thumbnail_url && (
              <div className="mt-2 relative w-32 aspect-video rounded-lg overflow-hidden border border-gray-200">
                <img src={form.thumbnail_url} alt="Preview" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => editingId ? handleUpdate(editingId) : handleAdd()} className="btn-gold gap-1 text-sm" data-testid="admin-video-save">
              <Check className="w-3.5 h-3.5" /> {editingId ? 'Guardar' : 'Añadir'}
            </Button>
            <Button onClick={resetForm} variant="outline" className="gap-1 text-sm" data-testid="admin-video-cancel">
              <X className="w-3.5 h-3.5" /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Video List */}
      {videos.length === 0 && !showForm ? (
        <div className="text-center py-8 bg-gray-50/50 rounded-lg">
          <Film className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-[hsl(var(--text-tertiary))]">Aún no hay vídeos</p>
          <p className="text-xs text-gray-300 mt-1">Pulsa "Añadir vídeo" para subir la primera clase</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-video-list">
          {videos.map((video, idx) => (
            <div
              key={video.id}
              className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg hover:border-[hsl(var(--accent))]/20 transition-colors"
              data-testid={`admin-video-item-${video.id}`}
            >
              <div className="text-gray-300">
                <GripVertical className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-[hsl(var(--text-primary))] truncate">{video.title}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    (video.video_source || 'youtube') === 'youtube'
                      ? 'bg-red-50 text-red-500'
                      : 'bg-blue-50 text-blue-500'
                  }`}>
                    {getSourceLabel(video)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-[hsl(var(--text-tertiary))] mt-0.5">
                  {video.class_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(video.class_date)}</span>}
                  {video.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{video.duration}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => startEdit(video)}
                  className="p-1.5 text-gray-400 hover:text-[hsl(var(--accent))] rounded-md hover:bg-gray-50 transition-colors"
                  title="Editar"
                  data-testid={`admin-edit-video-${video.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(video.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                  title="Eliminar"
                  data-testid={`admin-delete-video-${video.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-300 mt-4">
        Admite URLs de YouTube y Google Drive. Los vídeos de Drive deben estar compartidos como "Cualquier persona con el enlace".
      </p>
    </div>
  );
};

export default AdminClassVideos;
